const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;

// Guard to ensure we only identify a user once per page load
let hasIdentified = false;
let mixpanelInstance = null;
let mixpanelLoadPromise = null;

// Utility function to check if we're in a browser environment
const isBrowser = () => typeof window !== 'undefined';

const loadMixpanel = () => {
  if (!isBrowser() || !MIXPANEL_TOKEN) {
    return Promise.resolve(null);
  }

  if (mixpanelInstance) {
    return Promise.resolve(mixpanelInstance);
  }

  if (!mixpanelLoadPromise) {
    mixpanelLoadPromise = import('mixpanel-browser')
      .then(({ default: mixpanel }) => {
        mixpanel.init(MIXPANEL_TOKEN, {
          autocapture: true,
          record_sessions_percent: 100,
        });
        mixpanelInstance = mixpanel;
        return mixpanelInstance;
      })
      .catch(() => {
        mixpanelLoadPromise = null;
        return null;
      });
  }

  return mixpanelLoadPromise;
};

// Utility function to safely track events
const safeTrack = (eventName, properties = {}) => {
  loadMixpanel().then(mixpanel => {
    if (mixpanel) {
      mixpanel.track(eventName, properties);
    }
  });
};

export const clearMixpanelIdentification = () => {
  if (!isBrowser()) return;

  safeTrack("Logout");
  loadMixpanel().then(mixpanel => {
    if (mixpanel) {
      mixpanel.reset();
    }
  });

  hasIdentified = false;
};


// Identify the current user in Mixpanel and attach basic profile properties
export const identifyUser = currentUser => {
  if (hasIdentified || !currentUser) {
    return false;
  }

  // Avoid running in non-browser environments
  if (!isBrowser()) {
    return false;
  }

  const userId = currentUser?.id?.uuid;
  const name = currentUser?.attributes?.profile?.displayName;
  const email = currentUser?.attributes?.email;
  const createdAt = currentUser?.attributes?.createdAt;
  const city = currentUser?.attributes?.profile?.publicData?.city || currentUser?.attributes?.profile?.publicData?.address;
  // Persona status is written to metadata by the webhook; members can edit publicData.
  const persona_status = currentUser?.attributes?.profile?.metadata?.personaVerificationStatus || 'pending';

  if (!userId) {
    return false;
  }

  const payload = {
    $name: name,
    $email: email,
    $created: createdAt,
    profile_city: city,
    created_at: createdAt,
    sharetribe_user_id: userId,
    persona_status: persona_status,
  }

  hasIdentified = !!userId;
  // Set the distinct id and basic people properties after the analytics chunk loads.
  loadMixpanel().then(mixpanel => {
    if (!mixpanel) {
      return;
    }
    mixpanel.identify(userId);
    mixpanel.people.set(payload);
  });

  return true;
};

// Update Mixpanel people profile with published listings count
export const setPublishedListingsCount = (count, forceUpdate = false) => {
  // Only attempt to update if we have already identified the user
  if ((!hasIdentified && !forceUpdate) || !isBrowser()) {
    return;
  }

  loadMixpanel().then(mixpanel => {
    if (mixpanel) {
      mixpanel.people.set({
        number_of_published_listings: count,
      });
    }
  });
};

export default loadMixpanel;
